import React from 'react';
import cn from 'classnames';
import AuthStore from 'stores/AuthStore';
import CoachQuickActions from 'components/pure/call-card/coaching-quick-actions/CoachQuickActions';
import RepQuickActions from 'components/pure/call-card/coaching-quick-actions/RepQuickActions';
import FileUploadActions from 'actions/files/FileUploadActions';
import connectToStores from 'alt-utils/lib/connectToStores';
import FileUploadStore from 'stores/files/FileUploadStore';
import './CoachingQuickActions.styl';
import CoachingPlaylistStore, {PLAYLIST_VISIBILITY} from 'stores/CoachingPlaylistStore';
import AddToPlanQuickAction from 'components/pure/call-card/coaching-quick-actions/AddToPlanQuickAction';
import {List} from 'immutable';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import CoachingPlanActions from 'sources/coaching/CoachingPlanSource';
import CoachingReviewActions from 'sources/coaching/CoachingReviewSource';
import CoachingReviewFileUpload from 'records/CoachingReviewFileUpload';
import CoachingContentDetailScore from 'records/CoachingContentDetailScore';
import User from 'records/User';
import UserActions from 'actions/UserActions';

/**
 * Coaching quick actions are a few context menus that launch the user into
 * quickly assigning calls to plans, creating new plans, requesting feedback, etc.
 */
@connectToStores
export default class CoachingQuickActions extends React.Component {
  constructor(props) {
    super(props);

    this.onShowMenu = this.onShowMenu.bind(this);
    this.associateCallWithPlan = this.associateCallWithPlan.bind(this);
  }

  static getStores() {
    return [
      FileUploadStore,
      AuthStore,
      CoachingPlaylistStore,
      CoachingPlanStore,
      CoachingReviewStore,
    ];
  }

  static getPropsFromStores(props) {
    const playlistStore = CoachingPlaylistStore.getState();
    const planStore = CoachingPlanStore.getState();
    const plan = planStore.planMap.get(playlistStore.planId);
    const review = plan && plan.getNextReview();
    const reviewFileUploads = CoachingReviewStore.getState().reviewFileUploads.get(
      review && review.id,
      List()
    );
    const {caller} = props;
    // TODO Ideally would be converted in search source.
    // TODO sometimes it's a user (fullscreen call card), sometimes it's an object (search
    //  results). Fun!
    const rep = caller ? (caller instanceof User ? caller : new User(caller)) : null;
    return {
      fileUpload: FileUploadStore.getState().fileUploadsMap.get(props.fileUploadId),
      authUser: AuthStore.getState().user,
      playlistShown: playlistStore.visibility !== PLAYLIST_VISIBILITY.hidden,
      shouldFlash: playlistStore.shouldFlash,
      flashOnOpen: playlistStore.flashOnOpen,
      canAddCalls: playlistStore.canAddCalls,
      plan,
      review,
      reviewFileUploads,
      rep,
    };
  }

  onShowMenu() {
    const {fileUpload, fileUploadId} = this.props;
    // Get all the coaches if the user has access to the wizard.
    const authUser = AuthStore.getState().user;
    if (authUser.isCoach) {
      UserActions.fetchManagersAndCoaches.defer();
    }
    if (!fileUpload) {
      FileUploadActions.findFilesById.defer([fileUploadId]);
    }
  }

  getCoachingLabel(isCoachingProvided, isCoachingRequested) {
    if (isCoachingProvided)
      return <span className="coaching-requested-label-active">Coaching Provided</span>;

    return (
      <span className="coaching-requested-label">
        {isCoachingRequested ? 'Coaching Requested' : 'No Coaching'}
      </span>
    );
  }

  /**
   * Once we've saved the coaching plan, add the call that the user created
   * the plan from.  Note that this is very similar to what adhoc does
   * (see AdHocCoachingDialogContainer/CoachingPlan.createCoachingPlanWithCall)
   * consider combining if more work is done in this area
   *
   * @param plan - the newly created plan
   */
  async associateCallWithPlan(plan) {
    const {fileUpload, authUser} = this.props;

    const retrievedPlan = await CoachingPlanActions.retrieve(plan.id, {
      includeReviews: true,
    });
    const review = retrievedPlan.reviews.first();

    if (fileUpload.id && review) {
      await CoachingReviewActions.updateReviewFileUploads(
        review.id,
        List([
          new CoachingReviewFileUpload({
            addedByUserId: List([authUser.id]),
            fileuploadId: fileUpload.id,
            reviewFeedbackList: retrievedPlan.contentDetails.map(
              contentDetail => new CoachingContentDetailScore({contentDetail})
            ),
          }),
        ])
      );
    }
  }

  renderActions() {
    const {rep, authUser, canAddCalls, playlistShown} = this.props;
    if (!rep) {
      return null;
    }
    const authUserIsCaller = rep.id === authUser.id;

    if (playlistShown && canAddCalls) {
      return <AddToPlanQuickAction {...this.props} />;
    }

    if (authUserIsCaller) {
      return <RepQuickActions {...this.props} onShowMenu={this.onShowMenu} />;
    }

    if (authUser.isCoach) {
      const adminId = authUser.isAdmin() ? authUser.id : null;
      const managerId = authUser.role === 'manager' ? authUser.id : null;
      const coachId = authUser.isCoach && !authUser.isManager() ? authUser.id : null;
      return (
        <CoachQuickActions
          {...this.props}
          rep={rep}
          onShowMenu={this.onShowMenu}
          // use the refresh callback to grab the newly saved plan and attach
          // the appropriate call
          refreshCoachingData={this.associateCallWithPlan}
          coachId={coachId}
          managerId={managerId}
          adminId={adminId}
          includesCallsOnly
        />
      );
    }

    return null;
  }

  render() {
    const {isCoachingProvided, isCoachingRequested, newCallCard} = this.props;
    const coachingLabel = this.getCoachingLabel(isCoachingProvided, isCoachingRequested);

    return (
      <div
        onClick={e => e.stopPropagation()}
        className={cn('coaching-quick-actions-wrapper', {'new-call-card': newCallCard})}>
        {!newCallCard && coachingLabel}
        {this.renderActions()}
      </div>
    );
  }
}
